import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Loader2, TrendingUp, RefreshCw } from "lucide-react";

const CONF = {
  High: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Medium: "bg-amber-100 text-amber-800 border-amber-200",
};

export default function PromotionRadar() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/promotion-radar");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: true });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (<Card className="p-4 border-slate-200" data-testid="promotion-radar-loading"><div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin"/>Scanning for promotions…</div></Card>);
  if (!data || data.forbidden || data.error) return null;

  const rows = data.managers || [];

  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="promotion-radar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-emerald-600"/>
          <h3 className="font-semibold text-slate-900 text-sm">Promotion Radar</h3>
          <span className="text-xs text-slate-500">{rows.length} candidate(s)</span>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="h-7 px-2" data-testid="promotion-radar-refresh"><RefreshCw className="h-3.5 w-3.5"/></Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 py-4 text-center" data-testid="promotion-radar-empty">No promotion candidates yet. Keep raising the standard.</div>
      ) : (
        <div className="space-y-1.5" data-testid="promotion-radar-list">
          {rows.map((m) => (
            <div key={m.userId} className="p-2.5 rounded-md bg-slate-50 border border-slate-100" data-testid={`promotion-row-${m.userId}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`border text-[10px] px-1.5 py-0 ${CONF[m.confidence] || CONF.Medium}`}>{m.confidence}</Badge>
                <span className="text-sm font-medium text-slate-900 truncate">{m.name}</span>
                <span className="text-[11px] text-slate-500">{m.currentRole} → <span className="font-semibold text-emerald-700">{m.suggestedRole}</span></span>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {(m.reasons || []).slice(0, 3).map((r, i) => (
                  <span key={i}>{r}{i < Math.min(m.reasons.length, 3) - 1 ? " · " : ""}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
